<template>
  <transition name="stm-fade">
    <div
      v-if="visible"
      class="stm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pctm-title"
      @mousedown.self="$emit('close')"
    >
      <div class="stm-panel" @mousedown.stop>
        <button type="button" class="stm-close" :aria-label="'Close'" @click="$emit('close')">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div class="stm-header">
          <span class="stm-header__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="8" width="13" height="10" rx="1" />
              <path d="M16 11h3l3 3v4h-6V11z" />
              <circle cx="7.5" cy="19.5" r="1.5" />
              <circle cx="17.5" cy="19.5" r="1.5" />
            </svg>
          </span>
          <h3 id="pctm-title" class="stm-header__title">Professional Courier Rate Chart</h3>
        </div>

        <p class="stm-intro">
          Shipping is based on the
          <strong>customer's destination state</strong>
          and the <strong>parcel weight</strong>.
        </p>

        <div v-if="stateName" class="stm-current">
          <div class="stm-current__row">
            <span class="stm-current__label">Current customer</span>
            <strong class="stm-current__value">{{ stateName }}</strong>
          </div>
          <span v-if="activeKey" class="stm-current__slab">{{ activeRowLabel }} rate</span>
        </div>

        <div class="stm-table-wrap">
          <table class="stm-table">
            <thead>
              <tr>
                <th class="stm-table__weight">Destination</th>
                <th>100 g</th>
                <th>500 g</th>
                <th>1 kg</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.key" :class="{ 'is-active-row': row.key === activeKey }">
                <td class="stm-table__weight" :class="{ 'is-active': row.key === activeKey }">{{ row.label }}</td>
                <td
                  v-for="(val, i) in row.values"
                  :key="i"
                  :class="{ 'is-active': row.key === activeKey }"
                >
                  ₹{{ val }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ul class="stm-notes">
          <li><span class="stm-notes__star">*</span> &ldquo;North India&rdquo; covers states not separately listed above.</li>
          <li>Rates per Professional Couriers &mdash; New Rate Tariff 2024.</li>
        </ul>

        <div class="stm-actions">
          <button type="button" class="stm-btn" @click="$emit('close')">Got it</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
// Destination-state tariff for The Professional Couriers, transcribed
// from the printed "New Rate Tariff 2024" chart. Unlike India Post,
// Professional Courier prices by destination state (not warehouse
// distance) and tops out at the 1 kg column on the printed chart.
const ROWS = [
  { key: 'kerala',    label: 'Kerala',         values: [50, 60, 70] },
  { key: 'tamilnadu', label: 'Tamil Nadu',     values: [80, 100, 120] },
  { key: 'karnataka', label: 'Karnataka',      values: [80, 100, 120] },
  { key: 'andhra',    label: 'Andhra Pradesh', values: [90, 110, 150] },
  { key: 'north',     label: 'North India',    values: [100, 150, 180] },
  { key: 'mumbai',    label: 'Mumbai',         values: [100, 150, 180] },
  { key: 'delhi',     label: 'Delhi',          values: [100, 150, 180] },
  { key: 'kolkata',   label: 'Kolkata',        values: [100, 150, 180] }
]

export default {
  name: 'ProfessionalCourierTariffModal',
  props: {
    visible: { type: Boolean, default: false },
    stateName: { type: String, default: null }
  },
  data () {
    return { rows: ROWS }
  },
  computed: {
    // Best-effort match of the customer's state to a chart row. Rows
    // for Mumbai / Delhi / Kolkata / North India all carry the same
    // 100/150/180 price, so any unlisted state safely falls through to
    // the "North India" catch-all without mispricing.
    activeKey () {
      const s = String(this.stateName || '').trim().toLowerCase()
      if (!s) return ''
      if (s.includes('kerala')) return 'kerala'
      if (s.includes('tamil')) return 'tamilnadu'
      if (s.includes('karnataka')) return 'karnataka'
      if (s.includes('andhra') || s.includes('telangana')) return 'andhra'
      if (s.includes('maharashtra') || s.includes('mumbai')) return 'mumbai'
      if (s.includes('delhi')) return 'delhi'
      if (s.includes('west bengal') || s.includes('kolkata')) return 'kolkata'
      return 'north'
    },
    activeRowLabel () {
      const row = this.rows.find((r) => r.key === this.activeKey)
      return row ? row.label : ''
    }
  },
  watch: {
    visible (val) {
      if (val) {
        document.addEventListener('keydown', this.handleKeydown)
      } else {
        document.removeEventListener('keydown', this.handleKeydown)
      }
    }
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    handleKeydown (e) {
      if (e.key === 'Escape') this.$emit('close')
    }
  }
}
</script>

<style scoped>
.stm-overlay {
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}

.stm-panel {
  position: relative;
  width: 100%;
  max-width: 720px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.32), 0 4px 12px rgba(15, 23, 42, 0.08);
  padding: 1.4rem 1.4rem 1.15rem;
  animation: stm-pop 0.28s cubic-bezier(0.18, 1.2, 0.4, 1) both;
  max-height: calc(100vh - 2.5rem);
  overflow-y: auto;
}

.stm-close {
  position: absolute;
  top: 0.55rem;
  right: 0.55rem;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.stm-close:hover { background: #f3f4f6; color: #1f2a44; }
.stm-close:focus { outline: none; }
.stm-close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.stm-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.7rem;
  padding-right: 1.8rem;
}

.stm-header__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ecfdf5;
  color: #0e5b46;
  flex: 0 0 auto;
}

.stm-header__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0e5b46;
}

.stm-intro {
  margin: 0 0 0.85rem;
  font-size: 0.85rem;
  color: #4b5563;
  line-height: 1.45;
}

.stm-current {
  margin: 0 0 0.85rem;
  padding: 0.55rem 0.85rem;
  border-radius: 10px;
  background: linear-gradient(135deg, #ecfdf5 0%, #f5fbf8 100%);
  border: 1px solid #c7ebda;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.stm-current__row {
  display: inline-flex;
  align-items: baseline;
  gap: 0.4rem;
  min-width: 0;
}

.stm-current__label {
  font-size: 0.74rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #4f7868;
}

.stm-current__value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0e5b46;
}

.stm-current__slab {
  font-size: 0.74rem;
  font-weight: 600;
  color: #0e5b46;
  background: #ffffff;
  border: 1px solid #c7ebda;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
}

.stm-table-wrap {
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow-x: auto;
  margin-bottom: 0.85rem;
}

.stm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.stm-table th,
.stm-table td {
  padding: 0.5rem 0.65rem;
  text-align: center;
  border-bottom: 1px solid #eef1f7;
  border-right: 1px solid #eef1f7;
  color: #1f2a44;
  white-space: nowrap;
}

.stm-table th:last-child,
.stm-table td:last-child { border-right: 0; }

.stm-table tbody tr:last-child td { border-bottom: 0; }

.stm-table thead th {
  background: #fbfcfe;
  font-weight: 700;
  color: #4f5560;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.stm-table__weight {
  text-align: left !important;
  font-weight: 600;
  color: #1f2a44;
  background: #fbfcfe;
}

.stm-table tbody td.is-active {
  background: #ecfdf5;
  color: #0e5b46;
  font-weight: 700;
}

.stm-table tbody tr.is-active-row td:first-child {
  box-shadow: inset 2px 0 0 #16a07a;
}

.stm-notes {
  margin: 0 0 1rem;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.stm-notes li {
  font-size: 0.74rem;
  color: #6b7280;
  line-height: 1.4;
}

.stm-notes__star {
  color: #16a07a;
  font-weight: 700;
  margin-right: 0.15rem;
}

.stm-actions {
  display: flex;
  justify-content: flex-end;
}

.stm-btn {
  height: 38px;
  min-width: 110px;
  padding: 0 1.1rem;
  border-radius: 9px;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 0.01em;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  font-family: inherit;
}

.stm-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.3);
  filter: brightness(1.04);
}

.stm-btn:active { transform: translateY(0); filter: brightness(0.96); }
.stm-btn:focus { outline: none; }
.stm-btn:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22), 0 0 0 3px rgba(22, 160, 122, 0.3);
}

.stm-fade-enter-active,
.stm-fade-leave-active { transition: opacity 0.18s ease; }
.stm-fade-enter,
.stm-fade-leave-to { opacity: 0; }

@keyframes stm-pop {
  0%   { opacity: 0; transform: translateY(8px) scale(0.97); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

@media (max-width: 575px) {
  .stm-panel { padding: 1.1rem 1rem 0.95rem; }
  .stm-current { flex-direction: column; align-items: flex-start; }
}
</style>
